import styled, { Interpolation } from '@emotion/styled'
import { calcRem, breakpoint } from 'component-library/__styling/style-functions'
import { zIndex, easing, colors } from 'component-library/__styling/settings'
import { PlayButtonProps } from './PlayButton'
import { themedValue } from 'component-library/__styling/themed-value'
import { Theme } from 'component-library/__styling/themes'
import { hexToRGBA } from 'component-library/__styling/style-mixins'
import { withClass } from 'common/emotion'
import { conditionalStyles } from 'component-library/__styling/conditional-styles'

interface BorderWidth {
    borderWidth: string
}

interface LeftSize {
    left: number
}

interface BreakpointSizeIcon {
    sm: LeftSize
    default: BorderWidth & LeftSize
}

interface BreakpointSize {
    sm: number
    default: number
}

interface PlayButtonSize {
    container: BreakpointSize
    icon: BreakpointSizeIcon
}

const sevenSizes: PlayButtonSize = {
    container: {
        sm: 70,
        default: 60,
    },
    icon: {
        sm: {
            left: 29,
        },
        default: { left: 23, borderWidth: `${calcRem(12)} 0 ${calcRem(12)} ${calcRem(18)}` },
    },
}

const standardSizes: PlayButtonSize = {
    container: {
        sm: 100,
        default: 80,
    },
    icon: {
        sm: { left: 40 },
        default: {
            borderWidth: `${calcRem(17)} 0 ${calcRem(17)} ${calcRem(26)}`,
            left: 30,
        },
    },
}

const sizing = {
    thewest: standardSizes,
    perthnow: standardSizes,
    sevennews: sevenSizes,
}

export const playButtonClass = 'video_play_button'
export const playButtonIconClass = 'video_play_button_icon'

const internalPlayButtonStates = (theme: Theme, isInversed?: boolean) => {
    return {
        backgroundColor: themedValue(theme, {
            thewest: isInversed ? colors.black : colors.white,
            perthnow: colors.perthnow.pinkThulite,
            sevennews: colors.white,
        }),
        outline: 'none',

        '&::before': {
            borderLeftColor: themedValue(theme, {
                thewest: isInversed ? colors.white : colors.black,
                perthnow: isInversed ? colors.perthnow.greyBasalt : colors.white,
                sevennews: undefined,
            }),
        },
    }
}

export const playButtonStates = (theme: Theme) => {
    return {
        '&:hover, &:active, &:focus': {
            [`& .${playButtonClass}`]: {
                ...internalPlayButtonStates(theme),
            },
        },
    }
}

type StyledPlayButtonProps = Omit<
    PlayButtonProps & { isSmall?: boolean; isIcon?: boolean },
    'onClick'
>

const StyledPlayElementStyles: Interpolation<StyledPlayButtonProps & { theme: Theme }>[] = [
    {
        position: 'absolute',
        zIndex: zIndex.common.videoPlayButton,
        margin: 0,
        padding: 0,
        width: calcRem(44),
        height: calcRem(44),
        borderRadius: '100%',
        fontSize: 0,
        lineHeight: 1,
        transition: `opacity ${easing.easeOut.med}, background-color ${easing.easeOut.fast}`,
        cursor: 'pointer',
        border: 'none',

        '&::before': {
            position: 'absolute',
            top: '50%',
            left: calcRem(15),
            display: 'block',
            width: 0,
            height: 0,
            borderWidth: `${calcRem(12)} 0 ${calcRem(12)} ${calcRem(18)}`,
            borderStyle: 'solid',
            borderColor: 'transparent',
            content: `''`,
            transition: `border-color ${easing.easeOut.fast}`,
            transform: 'translateY(-50%)',
        },

        ['& > span']: {
            display: 'hidden',
        },
    },
    ({ theme, isCentered, isInversed, isIcon }) => ({
        backgroundColor: themedValue(theme, {
            thewest: isInversed ? theme.sectionValues.primaryColor : colors.white,
            perthnow: !isCentered ? colors.perthnow.pinkThulite : colors.perthnow.greyBasalt80,
            sevennews: hexToRGBA(colors.white, 0.8),
        }),

        '&::before': {
            borderLeftColor: themedValue(theme, {
                thewest: isInversed ? colors.white : theme.sectionValues.primaryColor,
                perthnow: colors.white,
                sevennews: section => section.video.primaryColor,
            }),
        },

        // Only want hover/active/focus when a button is rendered
        ...conditionalStyles(isIcon !== true, {
            '&:hover, &:active, &:focus': {
                ...internalPlayButtonStates(theme, isInversed),
            },
        }),
    }),
    // isCentered can probably be moved away from here just used for TitleOverlay for PN where flex is used
    ({ isCentered, theme, isSmall, isSmallIcon }) => {
        // !isCentered is just the previous behaviour, not setting sizing for each theme
        if (!isCentered) {
            return {
                top: 'auto',
                bottom: calcRem(16),
                left: calcRem(16),

                [breakpoint('sm')]: {
                    bottom: calcRem(20),
                    left: calcRem(23),
                    width: calcRem(64),
                    height: calcRem(64),
                },

                '&::before': {
                    [breakpoint('sm')]: {
                        left: isSmallIcon ? calcRem(25) : calcRem(23),
                        borderWidth: isSmallIcon
                            ? `${calcRem(12)} 0 ${calcRem(12)} ${calcRem(18)}`
                            : `${calcRem(17)} 0 ${calcRem(17)} ${calcRem(26)}`,
                    },
                },
            }
        }

        const defaultSize = isSmall ? 42 : sizing[theme.kind].container.default
        const defaultMargin = defaultSize / 2

        const smSize = isSmall ? 42 : sizing[theme.kind].container.sm
        const smMargin = smSize / 2

        const beforeSizing = isSmall
            ? {
                  left: calcRem(16),
                  borderWidth: `${calcRem(9)} 0 ${calcRem(9)} ${calcRem(14)}`,
              }
            : {
                  left: calcRem(sizing[theme.kind].icon.default.left),
                  borderWidth: sizing[theme.kind].icon.default.borderWidth,

                  [breakpoint('sm')]: {
                      left: calcRem(sizing[theme.kind].icon.sm.left),
                  },
              }

        return {
            top: '50%',
            left: '50%',
            marginLeft: `-${calcRem(defaultMargin)}`,
            marginTop: `-${calcRem(defaultMargin)}`,
            width: calcRem(defaultSize),
            height: calcRem(defaultSize),

            [breakpoint('sm')]: {
                top: '50%',
                left: '50%',
                marginLeft: `-${calcRem(smMargin)}`,
                marginTop: `-${calcRem(smMargin)}`,
                width: calcRem(smSize),
                height: calcRem(smSize),
            },

            '&::before': beforeSizing,
        }
    },
]

export const StyledPlayButton = withClass(playButtonClass)(
    styled('button')<StyledPlayButtonProps>(...StyledPlayElementStyles)
)

export const StyledPlayIcon = withClass(playButtonIconClass)(
    styled('div')<StyledPlayButtonProps>(...StyledPlayElementStyles)
)
